import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import MiniTiptap from './MiniTiptap';

import type { SinglePointRubricContent, RubricCriterion } from '@/shared/types/rubric';
import type { StoredStandard } from '@/shared/types/customStandard';
import { useSortableReorderSensors } from '@/shared/hooks/useSortableReorderSensors';
import { reorderModifiers } from '@/shared/utils/dndModifiers';
import { StandardsSelector } from '@/shared/components/StandardsSelector';

interface Props {
  content: SinglePointRubricContent;
  canEdit: boolean;
  ownerId: string;
  onChange: (content: SinglePointRubricContent) => void;
}

function sortedByPosition<T extends { position: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position);
}

function nextPosition(items: { position: number }[]): number {
  return items.reduce((m, i) => Math.max(m, i.position), 0) + 1000;
}

// ── Sortable criterion card ───────────────────────────────────────────────

interface SortableCriterionCardProps {
  criterion: RubricCriterion;
  canEdit: boolean;
  ownerId: string;
  criteriaCount: number;
  onUpdateTitle: (id: string, title: string) => void;
  onUpdateDescription: (id: string, desc: string) => void;
  onUpdateStandards: (id: string, standards: StoredStandard[]) => void;
  onRemove: (id: string) => void;
}

const SortableCriterionCard: React.FC<SortableCriterionCardProps> = ({
  criterion,
  canEdit,
  ownerId,
  criteriaCount,
  onUpdateTitle,
  onUpdateDescription,
  onUpdateStandards,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: criterion.id,
  });

  const style: React.CSSProperties = {
    // Translate, not Transform: dnd-kit's post-drop FLIP transform carries scaleX/scaleY
    // that visibly squash rows whose measured box changed during the drag.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
          {canEdit && (
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: 'grab',
                color: 'text.disabled',
                mt: 0.25,
                flexShrink: 0,
                '&:hover': { color: 'text.secondary' },
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
              {canEdit ? (
                <TextField
                  value={criterion.title}
                  onChange={(e) => onUpdateTitle(criterion.id, e.target.value)}
                  variant="standard"
                  size="small"
                  inputProps={{ style: { fontWeight: 600, fontSize: '1rem' } }}
                  sx={{ flex: 1 }}
                />
              ) : (
                <Typography variant="body1" fontWeight={600} sx={{ flex: 1 }}>
                  {criterion.title}
                </Typography>
              )}

              {canEdit && criteriaCount > 1 && (
                <Tooltip title="Remove criterion">
                  <IconButton size="small" color="error" onClick={() => onRemove(criterion.id)}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Proficient Description
            </Typography>
            <MiniTiptap
              content={criterion.description ?? ''}
              editable={canEdit}
              onUpdate={(html) => onUpdateDescription(criterion.id, html)}
              placeholder="Describe what proficient performance looks like..."
              minHeight={80}
            />

            <Box sx={{ mt: 1.5 }}>
              <StandardsSelector
                ownerId={ownerId}
                value={criterion.standards ?? []}
                onChange={(standards) => onUpdateStandards(criterion.id, standards)}
                label="Standards"
                disabled={!canEdit}
                scopeLabel="rubric"
              />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Main component ────────────────────────────────────────────────────────

const SinglePointEditor: React.FC<Props> = ({ content, canEdit, ownerId, onChange }) => {
  const criteria = useMemo(() => sortedByPosition(content.criteria), [content.criteria]);
  const sensors = useSortableReorderSensors();

  const addCriterion = useCallback(() => {
    const newCriterion: RubricCriterion = {
      id: crypto.randomUUID(),
      title: `Criterion ${criteria.length + 1}`,
      description: '',
      position: nextPosition(content.criteria),
      standards: [],
    };
    onChange({ ...content, criteria: [...content.criteria, newCriterion] });
  }, [content, criteria.length, onChange]);

  const removeCriterion = useCallback(
    (criterionId: string) => {
      onChange({
        ...content,
        criteria: content.criteria.filter((c) => c.id !== criterionId),
      });
    },
    [content, onChange]
  );

  const updateTitle = useCallback(
    (criterionId: string, title: string) => {
      onChange({
        ...content,
        criteria: content.criteria.map((c) => (c.id === criterionId ? { ...c, title } : c)),
      });
    },
    [content, onChange]
  );

  const updateDescription = useCallback(
    (criterionId: string, description: string) => {
      onChange({
        ...content,
        criteria: content.criteria.map((c) => (c.id === criterionId ? { ...c, description } : c)),
      });
    },
    [content, onChange]
  );

  const updateStandards = useCallback(
    (criterionId: string, standards: StoredStandard[]) => {
      onChange({
        ...content,
        criteria: content.criteria.map((c) => (c.id === criterionId ? { ...c, standards } : c)),
      });
    },
    [content, onChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const sorted = sortedByPosition(content.criteria);
      const fromIdx = sorted.findIndex((c) => c.id === active.id);
      const toIdx = sorted.findIndex((c) => c.id === over.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const reordered = arrayMove(sorted, fromIdx, toIdx);
      const newCriteria = reordered.map((c, i) => ({ ...c, position: (i + 1) * 1000 }));
      onChange({ ...content, criteria: newCriteria });
    },
    [content, onChange]
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each criterion describes the proficient-level expectation. Use separate columns for
        strengths and areas for growth during assessment.
      </Typography>

      <DndContext
        sensors={canEdit ? sensors : undefined}
        modifiers={reorderModifiers}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={criteria.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {criteria.map((criterion) => (
            <SortableCriterionCard
              key={criterion.id}
              criterion={criterion}
              canEdit={canEdit}
              ownerId={ownerId}
              criteriaCount={criteria.length}
              onUpdateTitle={updateTitle}
              onUpdateDescription={updateDescription}
              onUpdateStandards={updateStandards}
              onRemove={removeCriterion}
            />
          ))}
        </SortableContext>
      </DndContext>

      {canEdit && (
        <Button startIcon={<AddIcon />} onClick={addCriterion} variant="outlined" size="small">
          Add Criterion
        </Button>
      )}
    </Box>
  );
};

export default SinglePointEditor;
